/* eslint-disable ember/no-computed-properties-in-native-classes */
import DynamicFieldsFragment from '@linc-technologies/linced-common/models/dynamic-field/base-fragment';
import { computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import RelationshipEnum from '@linc-technologies/linced-common/enums/medical-relationship';
import FieldAccessRoles from '@linc-technologies/linced-common/enums/roles/field-access';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import type PermissionsService from '@linc-technologies/linced-common/services/permissions';
import type SessionService from '@linc-technologies/linced-common/services/session';
import { peekPerson } from '@linc-technologies/linced-common/utils/load-people';
import moment from 'moment';
import MedicalAccess from 'linced-app/enums/medical-access';
import type Person from 'linced-app/models/person';
import type RolesService from 'linced-app/services/roles';

// Proto: people.MedicalContact.
export default class MedicalContact extends DynamicFieldsFragment {
  declare [Type]: 'people/medical-contact';

  @service
  declare session: SessionService;

  @service
  declare roles: RolesService;

  @service
  declare permissions: PermissionsService;

  @attr('string', { defaultValue: '' })
  declare medicalContactId: string;

  // medicalPerson reads the medical contact's person from the store. It does
  // not load it. The route or the component must load the medical contacts
  // before render (loadPeople, or a type: DOCTORS_AND_SPECIALISTS query for
  // the whole school).
  get medicalPerson() {
    if (isEmpty(this.medicalContactId)) {
      return null;
    }
    return peekPerson<Person>(
      this.store,
      this.medicalContactId,
      'Load the medical contacts in the route afterModel (loadPeople, or a type: DOCTORS_AND_SPECIALISTS query) or in the task that opens the view.',
    );
  }

  @attr('number', { defaultValue: 9 })
  declare priority: number; // If not set from 1-9, default to 9 so they come last.

  @attr('number', { defaultValue: 0 })
  declare relationship: number;

  @computed('relationship')
  get relationshipName() {
    return RelationshipEnum.getLabel(this.relationship);
  }

  get fullName() {
    return this.medicalPerson?.fullName;
  }

  @attr('string', { defaultValue: '' })
  declare notes: string;

  @computed('endDate', 'startDate')
  get isCurrent() {
    const today = moment().format('YYYY-MM-DD');
    const hasStarted = isEmpty(this.startDate) || this.startDate <= today;
    const hasEnded = !isEmpty(this.endDate) && this.endDate < today;
    return hasStarted && !hasEnded;
  }

  @attr('string', { defaultValue: '' })
  declare startDate: string;

  @attr('string', { defaultValue: '' })
  declare endDate: string;

  @attr('string', { defaultValue: '' })
  declare externalId: string;

  @attr('boolean', { defaultValue: false })
  declare permissionToContact: boolean;

  @attr('number', { defaultValue: MedicalAccess.NONE })
  declare roleRequired: EnumValue<typeof MedicalAccess>;

  @computed('roleRequired', 'session.roles.[]')
  get userCanAccess() {
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      return true;
    }
    switch (this.roleRequired) {
      case MedicalAccess.NONE:
        return true;
      case MedicalAccess.STUDENT_MEDICAL:
        return this.roles.has(FieldAccessRoles.STUDENT_MEDICAL) as boolean;
      case MedicalAccess.STUDENT_SENSITIVE:
        return this.roles.has(FieldAccessRoles.STUDENT_SENSITIVE) as boolean;
    }
  }
}
